// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// render.ts — 143 cells, painted from data attributes.
//
// EXACTLY ONE BACKGROUND CHANNEL IS VISIBLE AT A TIME. The grid root carries a `data-view` of
// normal / cost / reach, and the stylesheet keys off it. Snow depth, the Dijkstra cost field and the
// reachable-set dim all want the same pixels, and layering them means a 28px cell carrying nine
// simultaneous signals — which is a legibility problem rather than a palette problem, and the fix is
// exclusion. Foreground marks have a fixed z-priority: colonist over designation over glyph, with
// carry / job-age / buried collapsed into ONE corner badge showing the highest-priority state.
//
// The DOM is built once and only attributes are written afterwards, so a repaint touches no layout
// beyond what actually changed.

import { COLS, ROWS, TILES, Terrain } from './map';
import { Build, MAX_DEPTH } from './modes';
import { INF, OUTDOOR, isDoor, isJammed } from './graph';
import { GLYPH } from './palette';
import { Res, State, fieldFrom } from './sim';

export type View = 'normal' | 'cost' | 'reach';

export interface Board {
  el: HTMLElement;
  cells: HTMLElement[];
  paint(s: State, opts: PaintOpts): void;
}

export interface PaintOpts {
  view: View;
  /** The tile a cost field or reachable set is computed from, or -1. */
  focus: number;
  selected: number;
  hint: number;
}

const BUILD_GLYPH: Record<number, string> = {
  [Build.Path + 1]: GLYPH.path,
  [Build.Wall + 1]: '',
  [Build.Door + 1]: GLYPH.door,
  [Build.Bed + 1]: GLYPH.bed,
  [Build.Stockpile + 1]: GLYPH.store,
  [Build.Firepit + 1]: GLYPH.pit,
  [Build.Stove + 1]: GLYPH.stove,
};

export function createBoard(): Board {
  const el = document.createElement('div');
  el.className = 'board';
  el.setAttribute('role', 'grid');
  el.setAttribute('aria-label', 'The colony');
  const cells: HTMLElement[] = [];
  for (let t = 0; t < TILES; t++) {
    const c = document.createElement('div');
    c.className = 'cell';
    c.dataset.t = String(t);
    c.setAttribute('role', 'gridcell');
    const g = document.createElement('span');
    g.className = 'glyph';
    c.appendChild(g);
    el.appendChild(c);
    cells.push(c);
  }
  return { el, cells, paint: (s, o) => paint(el, cells, s, o) };
}

function paint(root: HTMLElement, cells: HTMLElement[], s: State, o: PaintOpts): void {
  root.dataset.view = o.view;
  const field = o.focus >= 0 && o.view !== 'normal' ? fieldFrom(s, o.focus) : null;
  let maxCost = 1;
  if (field) for (let t = 0; t < TILES; t++) if (field[t] < INF && field[t] > maxCost) maxCost = field[t];

  const at: number[] = new Array(TILES).fill(-1);
  for (let i = 0; i < s.n; i++) if (s.alive[i]) at[s.pos[i]] = i;

  for (let t = 0; t < TILES; t++) {
    const c = cells[t];
    const b = s.build[t];
    const d = s.depth[t];
    const indoors = s.rooms[t] !== OUTDOOR;

    set(c, 'terr', String(s.terrain[t]));
    set(c, 'b', String(b));
    set(c, 'snow', d > 0 && b !== Build.Wall + 1 ? String(d) : '0');
    set(c, 'room', indoors ? 'in' : 'out');
    set(c, 'warm', indoors && s.warmRooms.has(s.rooms[t]) ? '1' : '0');
    set(c, 'desig', String(s.desig[t]));
    set(c, 'jam', isDoor(s.build, t) && isJammed(s.build, s.depth, t) ? '1' : '0');

    if (field) {
      const v = field[t];
      set(c, 'reach', v < INF ? '1' : '0');
      if (o.view === 'cost' && v < INF) {
        set(c, 'cost', '1');
        c.style.setProperty('--k', String(Math.round((v * 100) / maxCost)));
      } else {
        c.removeAttribute('data-cost');
      }
    } else {
      c.removeAttribute('data-reach');
      c.removeAttribute('data-cost');
    }

    const glyph = c.firstElementChild as HTMLElement;
    let text = '';
    if (b > 0) text = BUILD_GLYPH[b] ?? '';
    else if (s.terrain[t] === Terrain.Stand && s.regrow[t] <= s.tick) text = GLYPH.stand;
    else if (s.terrain[t] === Terrain.Forage && s.regrow[t] <= s.tick) text = GLYPH.forage;
    else if (s.terrain[t] === Terrain.Rock) text = '';
    if (glyph.textContent !== text) glyph.textContent = text;

    c.classList.toggle('hint', t === o.hint);

    // one colonist per cell — the topmost id standing here
    const who = at[t];
    let dot = c.querySelector('.who') as HTMLElement | null;
    if (who >= 0) {
      if (!dot) {
        dot = document.createElement('span');
        dot.className = 'who';
        c.appendChild(dot);
      }
      const initial = s.name[who].charAt(0);
      if (dot.textContent !== initial) dot.textContent = initial;
      dot.classList.toggle('ill', s.ill[who] === 1);
      dot.classList.toggle('sel', who === o.selected);
    } else if (dot) {
      dot.remove();
    }

    // ONE badge: buried store beats a ground pile beats a stale job.
    const pile = s.cont.find((x) => x.tile === t && x.kind === 1);
    const wants = pile ? (d >= 2 ? 'buried' : 'pile') : '';
    let badge = c.querySelector('.badge') as HTMLElement | null;
    if (wants) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'badge';
        c.appendChild(badge);
      }
      badge.style.background = wants === 'buried' ? 'var(--jam)' : 'var(--store)';
    } else if (badge) {
      badge.remove();
    }
  }
}

function set(el: HTMLElement, k: string, v: string): void {
  if (el.dataset[k] !== v) el.dataset[k] = v;
}

/** Depth-3 tiles are impassable; used by the layout probe and by the tutorial's hint ring. */
export function isBlocked(s: State, t: number): boolean {
  return s.depth[t] >= MAX_DEPTH || s.build[t] === Build.Wall + 1 || s.terrain[t] === Terrain.Rock;
}

export { COLS, ROWS, Res };
